import React, { useEffect, useState } from 'react';
import { Input, Message, Panel, Tag } from 'rsuite';
import { Button } from '@urbantxt/button';
import { openSuccessToaster } from 'services/toast.service';
import { BagelPrompt } from '../../../types';
import { testBagelAiFlowNode } from '../../../api/bagel-ai-flow';
import type { BagelAiFlowNode, BagelAiFlowNodeTestResult, BagelAiFlowRoute } from '../../types/aiFlow';
import FLOW_NODE_KIND_LABEL from '../../utils/flowNodeKindLabels';
import AutoGrowTextarea from '../AutoGrowTextarea';
import RouteList from './RouteList';
import styles from './AiFlowTab.module.scss';

interface PromptEditorPanelProps {
  prompt: BagelPrompt;
  node?: BagelAiFlowNode;
  routes?: BagelAiFlowRoute[];
  onSave: (name: string, content: string) => Promise<void>;
  readOnly?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

export default function PromptEditorPanel({
  prompt,
  node,
  routes = [],
  onSave,
  readOnly = false,
  onDirtyChange,
}: PromptEditorPanelProps) {
  const [content, setContent] = useState(prompt.content);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testInput, setTestInput] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<BagelAiFlowNodeTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const isDirty = content !== prompt.content;

  useEffect(() => {
    setContent(prompt.content);
  }, [prompt.content, prompt.name]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    setTestInput('');
    setTestResult(null);
    setTestError(null);
  }, [node?.id]);

  const handleRunTest = async () => {
    if (!node) return;
    setTestRunning(true);
    setTestError(null);
    setTestResult(null);
    try {
      const result = await testBagelAiFlowNode(node.id, content, testInput);
      setTestResult(result);
    } catch (err) {
      setTestError(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setTestRunning(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(prompt.name, content);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      openSuccessToaster('Prompt copied to clipboard.', 2000);
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  return (
    <Panel
      bordered
      header={
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {prompt.name}
            </span>
            {node ? <Tag>{FLOW_NODE_KIND_LABEL[node.kind]}</Tag> : null}
          </div>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            Version {prompt.version}
          </span>
        </div>
      }
    >
      {node?.description && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
            PIPELINE ROLE
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
            {node.description}
          </p>
        </div>
      )}
      {error && (
        <Message type="error" showIcon style={{ marginBottom: 8 }}>
          {error}
        </Message>
      )}
      <AutoGrowTextarea
        value={content}
        disabled={readOnly}
        onChange={v => {
          if (readOnly) return;
          setContent(String(v));
          setSaved(false);
        }}
        style={{ fontFamily: 'monospace', fontSize: 13, width: '100%' }}
      />
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 8,
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        {saved && (
          <span style={{ color: '#22c55e', fontSize: 13 }}>
            ✓ Saved — takes effect within 5 min
          </span>
        )}
        <Button
          appearance="subtle"
          size="sm"
          onClick={handleCopy}
          disabled={!content}
        >
          Copy
        </Button>
        {!readOnly ? (
          <Button
            appearance="primary"
            size="sm"
            onClick={handleSave}
            disabled={!isDirty || saving}
            loading={saving}
          >
            Save
          </Button>
        ) : null}
      </div>
      {node?.callsLlm ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>
            TEST THIS STEP
          </div>
          <div className={styles.testBox}>
            <div className={styles.testRow}>
              <Input
                as="textarea"
                rows={2}
                value={testInput}
                placeholder="Type a sample message to see what the AI decides…"
                onChange={value => setTestInput(String(value))}
                className={styles.testInput}
              />
              <Button
                appearance="primary"
                onClick={handleRunTest}
                loading={testRunning}
                disabled={!testInput.trim() || testRunning}
              >
                Run test
              </Button>
            </div>
            {testError ? <p className={styles.testError}>{testError}</p> : null}
            {testResult ? (
              <pre
                style={{
                  margin: 0,
                  padding: 10,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {testResult.resultType === 'text'
                  ? String(testResult.result)
                  : JSON.stringify(testResult.result, null, 2)}
              </pre>
            ) : null}
          </div>
        </div>
      ) : null}
      <RouteList routes={routes} />
    </Panel>
  );
}

export function PromptEditorPlaceholder({ message }: { message: string }) {
  return (
    <Panel bordered>
      <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>{message}</p>
    </Panel>
  );
}
